import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TabbedDetailListSkeleton, ROLE_DETAIL_TAB_WIDTHS } from "@/components/skeletons";
import AppButton from "@/components/buttons/AppButton";
import RoleGroups from "@/components/tabs/RoleGroupTab";
import RoleUsers from "@/components/tabs/RoleUserTab";
import { getGroupById, updateGroup } from "@/services/group.service";
import { getRoleById, getRoleGroups, getRoleUsers } from "@/services/role.service";
import { getUserById, updateUser } from "@/services/user.service";
import { BUTTONS, HEADINGS } from "@/utils/constants/appConstants/commonConstants";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import NoDataAvailable from '@/components/cards/NoDataAvailable';
import { SOMETHING_WENT_WRONG } from '@/utils/constants/messages';
import { useDataQueries } from '@/utils/hooks/useDataQueries';

const RoleDetails = () => {
    const [isUpdateRolePermitted, setIsUpdateRolePermission] = useState(false);
    const [isViewUserPermitted, setIsViewUserPermitted] = useState(false);
    const [isViewGroupPermitted, setIsViewGroupPermitted] = useState(false);
    const [isUpdateUserPermitted, setIsUpdateUserPermitted] = useState(false);
    const [roleDetails, setRoleDetails] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [roleUserLoading, setRoleUserLoading] = useState(false);
    const [roleGroupLoading, setRoleGroupLoading] = useState(false);
    const [roleUsers, setRoleUsers] = useState<any>([]);
    const [roleGroups, setRoleGroups] = useState([]);
    const [selectedGroupDetails, setSelectedGroupDetails] = useState<any>();
    const [selectedUserDetails, setSelectedUserDetails] = useState<any>();
    const [allFetched, setAllFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [usersLeft, setUsersLeft] = useState(true);
    const [activeTab, setActiveTab] = useState<"permissions" | "users" | "groups">("permissions");
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting, filter, setFilter, searchItem, setSearchItem, resetDataQueries] = useDataQueries();

    const currentOrganization = getCurrentOrganization();
    const params = useParams();
    const id = params.id ? params.id : "";
    const navigate = useNavigate();

    useEffect(() => {
        setIsUpdateRolePermission(isFeaturePermitted("AdminPortalUpdateRole"));
        setIsViewUserPermitted(isFeaturePermitted("AdminPortalViewUser"));
        setIsViewGroupPermitted(isFeaturePermitted("AdminPortalViewGroup"));
        setIsUpdateUserPermitted(isFeaturePermitted("AdminPortalUpdateUser"));
    }, []);

    useEffect(() => {
        setLoading(true);
        getRoleById(
            id,
            (response: any) => {
                setRoleDetails(response.data);
                setLoading(false);
            },
            (error: any) => {
                console.log(error);
                setLoading(false);
            }
        );
    }, []);

    useEffect(() => {
        setRoleGroupLoading(true);
        getRoleGroups(
            id,
            (response: any) => {
                setRoleGroupLoading(false);
                setRoleGroups(response.data.groups);
            },
            (error: any) => {
                setRoleGroupLoading(false);
                console.log(error);
            }
        );
    }, []);

    useEffect(() => {
        setUsersLeft(true);
        setAllFetched(false);
        setRoleUserLoading(true);
        getRoleUsers(
            id,
            dataLength,
            pageNo,
            (response: any) => {
                setRoleUserLoading(false);
                if (response.data?.users?.length < dataLength) {
                    setUsersLeft(false);
                    setAllFetched(true);
                }
                setRoleUsers(response.data.users);
            },
            (error: any) => {
                setRoleUserLoading(false);
                console.log(error);
            }
        );
    }, [dataLength]);

    window.onscroll = function () {
        if (allFetched || isLoading) {
            return;
        }
        if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1) {
            setPageNo(pageNo + 1);
            setIsLoading(true);
            if (usersLeft) {
                getRoleUsers(
                    id,
                    dataLength,
                    pageNo + 1,
                    (response: any) => {
                        setIsLoading(false);
                        if (response.data?.users?.length < dataLength) {
                            setUsersLeft(false);
                            setAllFetched(true);
                        }
                        const r_users = [...roleUsers, ...response.data.users];
                        setRoleUsers(r_users);
                    },
                    (error: any) => {
                        setIsLoading(false);
                        console.log(error);
                    }
                );
            }
        }
    };

    const getGroupDetails = (id: string) => {
        return getGroupById(
            id,
            (response: any) => {
                setSelectedGroupDetails(response.data);
            },
            (error: any) => {
                console.log(error);
            }
        );
    };

    useEffect(() => {
        if (!selectedGroupDetails) return;
        const group_roles = selectedGroupDetails.roles;
        const new_role_ids = group_roles.filter((role: any) => role.id !== roleDetails.id).map((role: any) => role.id);

        const data = JSON.stringify({
            roles: new_role_ids,
        });
        updateGroup(
            selectedGroupDetails.id,
            data,
            (response: any) => {
                const new_role_groups = roleGroups.filter((group: any) => group.id !== selectedGroupDetails.id);
                setRoleGroups(new_role_groups);
            },
            (error: any) => {
                console.log(error);
            }
        );
    }, [selectedGroupDetails]);

    const getUserDetails = (id: string) => {
        return getUserById(
            id,
            (response: any) => {
                setSelectedUserDetails(response.data);
            },
            (error: any) => {
                console.log(error);
            }
        );
    };

    useEffect(() => {
        if (!selectedUserDetails) return;
        const userOrganization = selectedUserDetails.organizations.filter((org: any) => org.organization === currentOrganization.organization);
        if (userOrganization?.length > 0) {
            const user_roles = userOrganization[0]?.roles;
            const new_role_ids = user_roles.filter((role: any) => role.id !== roleDetails.id).map((role: any) => role.id);

            const data = JSON.stringify({
                roles: new_role_ids,
            });

            updateUser(
                selectedUserDetails.id,
                data,
                (response: any) => {
                    const new_role_users = roleUsers.filter((user: any) => user.id !== selectedUserDetails.id);
                    setRoleUsers(new_role_users);
                },
                (error: any) => {
                    console.log(error);
                }
            );
        }
    }, [selectedUserDetails]);

    return loading ? (
        <TabbedDetailListSkeleton tabWidths={ROLE_DETAIL_TAB_WIDTHS} />
    ) : (
        <>
            <div className="gs-detail-header">
                <h3 className="gs-detail-title capitalize" id='role-detail-title'>{roleDetails.name}</h3>
                <div>
                    {isUpdateRolePermitted &&
                        !!roleDetails?.name &&
                        roleDetails?.name !== "superadmin" &&
                        !currentOrganization?.roles.some((obj: any) => obj.id === roleDetails.id) &&
                        currentOrganization.organization === roleDetails?.organization && (
                            <AppButton id='role-detail-edit-btn' onClick={() => navigate(`/role/${roleDetails?.id}/edit`)} btnText={BUTTONS.EDIT} isLoading={false} className={"btn-primary"} />
                        )}
                </div>
            </div>
            <ul className="gs-page-tabs">
                <li>
                    <button
                        className={`gs-page-tab${activeTab === "permissions" ? " gs-page-tab--active" : ""}`}
                        id='role-detail-permission-tab'
                        onClick={() => setActiveTab("permissions")}
                    >
                        {HEADINGS.PERMISSIONS}
                    </button>
                </li>
                <li>
                    <button
                        className={`gs-page-tab${activeTab === "users" ? " gs-page-tab--active" : ""}`}
                        id='role-detail-user-tab'
                        onClick={() => setActiveTab("users")}
                    >
                        {HEADINGS.USERS}
                    </button>
                </li>
                <li>
                    <button
                        className={`gs-page-tab${activeTab === "groups" ? " gs-page-tab--active" : ""}`}
                        id='role-detail-group-tab'
                        onClick={() => setActiveTab("groups")}
                    >
                        {HEADINGS.GROUPS}
                    </button>
                </li>
            </ul>
            <div>
                {activeTab === "permissions" && (
                    <div className="gs-list-page-card">
                        <ul className="gs-detail-list">
                            {roleDetails.features?.length ? (
                                roleDetails.features.map((feature: any, key: string) => (
                                    <li className="gs-detail-list-item" key={key}>
                                        <div className="gs-detail-list-name" id={`role-detail-permission-tab-feature-${key}`}>{feature}</div>
                                    </li>
                                ))
                            ) : (
                                <li>
                                    <NoDataAvailable>
                                        <h4 className="text-center" id='role-detail-permission-tab-something-went-wrong-message'>{SOMETHING_WENT_WRONG}</h4>
                                    </NoDataAvailable>
                                </li>
                            )}
                        </ul>
                    </div>
                )}
                {activeTab === "users" && (
                    <RoleUsers roleUsers={roleUsers} getUserDetails={getUserDetails} isLoading={roleUserLoading} dataLoad={isLoading} />
                )}
                {activeTab === "groups" && (
                    <RoleGroups roleGroups={roleGroups} getGroupDetails={getGroupDetails} isLoading={roleGroupLoading} />
                )}
            </div>
        </>
    );
};

export default RoleDetails;
